import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useSiteCustomization } from "@/context/SiteCustomizationContext";
import {
  ArrowRight,
  TrendingUp,
  Award,
  Users,
  Star,
  GraduationCap,
  ChevronRight
} from "lucide-react";

const DEFAULT_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/6a54yvph_WhatsApp%20Image%202026-02-09%20at%2011.16.41%20PM.jpeg",
  classroom: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/22gq6ict_WhatsApp%20Image%202026-02-09%20at%209.41.16%20PM.jpeg",
  students: "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/aw9tl6vm_WhatsApp%20Image%202026-02-10%20at%204.57.23%20PM.jpeg",
  examHall: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/t2tw76x1_WhatsApp%20Image%202026-02-09%20at%2011.10.54%20PM.jpeg",
};

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function SuccessStoriesPage() {
  const { getContent, config: siteConfig } = useSiteCustomization();
  const g = (key, fb) => getContent(key, fb);
  const getImg = (key, fallbackKey) => siteConfig?.images?.[key] || DEFAULT_IMAGES[fallbackKey || key] || "";

  const schoolResults = [
    { name: g("ss_school_1_name","Accra Academy JHS"), location: g("ss_school_1_location","Greater Accra"), before: parseInt(g("ss_school_1_before","62")), after: parseInt(g("ss_school_1_after","89")), students: g("ss_school_1_students","120"), year: g("ss_school_1_year","2025"), quote: g("ss_school_1_quote","Great Messiah's mock exams transformed our exam preparation. The alignment with GES syllabus made all the difference."), contact: g("ss_school_1_contact","Mr. Emmanuel Adjei, Headmaster") },
    { name: g("ss_school_2_name","Kumasi Methodist JHS"), location: g("ss_school_2_location","Ashanti Region"), before: parseInt(g("ss_school_2_before","55")), after: parseInt(g("ss_school_2_after","83")), students: g("ss_school_2_students","85"), year: g("ss_school_2_year","2025"), quote: g("ss_school_2_quote","Our students gained confidence knowing exactly what to expect. The marking schemes are invaluable for teachers."), contact: g("ss_school_2_contact","Mrs. Grace Owusu, Academic Head") },
    { name: g("ss_school_3_name","Tamale Central JHS"), location: g("ss_school_3_location","Northern Region"), before: parseInt(g("ss_school_3_before","48")), after: parseInt(g("ss_school_3_after","76")), students: g("ss_school_3_students","200"), year: g("ss_school_3_year","2025"), quote: g("ss_school_3_quote","Even in remote areas, digital delivery gave us instant access. Students practiced with real exam conditions."), contact: g("ss_school_3_contact","Alhaji Ibrahim Yakubu, Director") },
  ];

  const studentStories = [
    { name: g("ss_student_1_name","Abena Mensah"), school: g("ss_student_1_school","Osu Presbyterian JHS"), beforeScore: g("ss_student_1_before","Aggregate 24"), afterScore: g("ss_student_1_after","Aggregate 8"), subjects: g("ss_student_1_subjects","Mathematics, English, Science"), quote: g("ss_student_1_quote","The practice questions were very similar to the actual BECE. I knew what to expect and felt so confident during the exam!") },
    { name: g("ss_student_2_name","Kwame Asante"), school: g("ss_student_2_school","Achimota Basic School"), beforeScore: g("ss_student_2_before","Aggregate 20"), afterScore: g("ss_student_2_after","Aggregate 10"), subjects: g("ss_student_2_subjects","Social Studies, RME, ICT"), quote: g("ss_student_2_quote","The marking schemes helped me understand exactly how examiners want answers structured. My scores improved dramatically.") },
    { name: g("ss_student_3_name","Fatima Ibrahim"), school: g("ss_student_3_school","Tamale International School"), beforeScore: g("ss_student_3_before","Aggregate 30"), afterScore: g("ss_student_3_after","Aggregate 12"), subjects: g("ss_student_3_subjects","All Core Subjects"), quote: g("ss_student_3_quote","I was struggling with Integrated Science, but after doing the mock exams multiple times, I finally understood the exam pattern.") },
    { name: g("ss_student_4_name","Daniel Tetteh"), school: g("ss_student_4_school","Ho EP JHS"), beforeScore: g("ss_student_4_before","Aggregate 22"), afterScore: g("ss_student_4_after","Aggregate 9"), subjects: g("ss_student_4_subjects","Mathematics, French"), quote: g("ss_student_4_quote","My mathematics score went from a 5 to a 2. The step-by-step solutions in the marking schemes were a game-changer.") },
  ];

  const overallStats = [
    { value: g("ss_stat_1_value","95%"), label: g("ss_stat_1_label","Average Pass Rate") },
    { value: g("ss_stat_2_value","50+"), label: g("ss_stat_2_label","Partner Schools") },
    { value: g("ss_stat_3_value","28%"), label: g("ss_stat_3_label","Avg. Score Improvement") },
    { value: g("ss_stat_4_value","3000+"), label: g("ss_stat_4_label","Students Helped") },
  ];

  return (
    <div className="min-h-screen" data-testid="success-stories-page">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${getImg("successHero", "hero")}')` }} />
        <div className="absolute inset-0 bg-navy-900/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <Award className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-white/90 font-medium">{g("ss_hero_badge", "Proven Results Across Ghana")}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="success-hero-heading">
              {g("ss_hero_title", "Real Students.")}{" "}
              <span className="text-accent">{g("ss_hero_highlight", "Real Results.")}</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-xl">
              {g("ss_hero_subtitle", "See how schools and students across Ghana have achieved remarkable BECE improvements with Great Messiah Publication mock exams.")}
            </p>
            <Button
              asChild
              className="btn-pill bg-accent hover:bg-accent/90 text-white h-14 text-base shadow-lg shadow-accent/25 group"
              data-testid="success-browse-btn"
            >
              <Link to="/mock-exams">
                {g("ss_hero_cta", "Start Your Journey")}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Overall Stats */}
      <section className="py-16 bg-white" data-testid="success-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {overallStats.map((stat, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="text-center">
                  <p className="text-4xl md:text-5xl font-bold text-navy-900 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-gray-500 text-sm uppercase tracking-wider">{stat.label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* School Results - Before/After */}
      <section className="py-24 bg-gray-50" data-testid="school-results-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <p className="text-accent font-semibold uppercase tracking-wider mb-3 text-sm">{g("ss_schools_label", "School Performance")}</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-4">
              {g("ss_schools_title", "Schools That Transformed Results")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              {g("ss_schools_subtitle", "Compare BECE pass rates before and after partnering with Great Messiah Publication")}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {schoolResults.map((school, index) => (
              <AnimatedSection key={index} delay={index * 120}>
                <motion.div
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden h-full"
                  whileHover={{ y: -6, boxShadow: "0 20px 50px rgba(0,0,0,0.1)" }}
                  transition={{ duration: 0.3 }}
                  data-testid={`school-result-${index}`}
                >
                  {/* Before/After Bar */}
                  <div className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-navy-900 text-lg">{school.name}</h3>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{school.year}</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-6">{school.location}</p>

                    {/* Before/After Comparison */}
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="text-gray-500">Before</span>
                          <span className="font-bold text-gray-700">{school.before}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                          <motion.div
                            className="h-full bg-gray-400 rounded-full"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${school.before}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.3 }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="text-accent font-medium">After</span>
                          <span className="font-bold text-accent">{school.after}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-accent to-red-400 rounded-full"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${school.after}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, delay: 0.5 }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Improvement badge */}
                    <div className="mt-4 flex items-center gap-2">
                      <div className="flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                        <TrendingUp className="w-4 h-4" />
                        +{school.after - school.before}% improvement
                      </div>
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="px-6 pb-6 pt-2">
                    <p className="text-sm text-gray-600 italic mb-3">"{school.quote}"</p>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">{school.students} students | {school.contact}</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Student Success Stories */}
      <section className="py-24 bg-white" data-testid="student-stories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Image side */}
            <AnimatedSection>
              <div className="sticky top-28">
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={getImg("studentJourneys", "students")}
                    alt="Students studying in classroom"
                    className="w-full h-80 lg:h-[500px] object-cover"
                  />
                </motion.div>
                <motion.div
                  className="absolute -bottom-4 -right-4 bg-accent text-white rounded-xl p-4 shadow-xl shadow-accent/30"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-6 h-6" />
                    <div>
                      <p className="text-2xl font-bold">{g("ss_stat_3_value", "28%")}</p>
                      <p className="text-xs text-white/80">{g("ss_improvement_label", "Avg. Improvement")}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Stories side */}
            <div>
              <AnimatedSection>
                <p className="text-accent font-semibold uppercase tracking-wider mb-3 text-sm">{g("ss_students_label", "Student Journeys")}</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-10">
                  {g("ss_students_title", "From Struggling to Excelling")}
                </h2>
              </AnimatedSection>

              <div className="space-y-6">
                {studentStories.map((story, index) => (
                  <AnimatedSection key={index} delay={index * 120}>
                    <motion.div
                      className="bg-gray-50 rounded-2xl p-6 border border-gray-100 group"
                      whileHover={{ x: 6, backgroundColor: "#f8fafc" }}
                      transition={{ duration: 0.3 }}
                      data-testid={`student-story-${index}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-navy-900">{story.name}</h4>
                          <p className="text-sm text-gray-500">{story.school}</p>
                        </div>
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>

                      {/* Score comparison */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-gray-200 text-gray-600 px-3 py-1.5 rounded-lg text-sm font-mono">
                          {story.beforeScore}
                        </div>
                        <div className="flex items-center gap-1 text-green-600">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                        <div className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-sm font-mono font-bold">
                          {story.afterScore}
                        </div>
                        <div className="ml-auto">
                          <span className="text-xs text-gray-400">{story.subjects}</span>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 italic">"{story.quote}"</p>
                    </motion.div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden" data-testid="success-cta">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${getImg("examHall")}')` }} />
        <div className="absolute inset-0 bg-navy-900/90" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {g("ss_cta_title", "Your Success Story Starts Here")}
            </h2>
            <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
              {g("ss_cta_subtitle", "Join thousands of students and schools who have improved their BECE results with our GES-aligned mock examinations.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="btn-pill bg-accent hover:bg-accent/90 text-white text-base h-14 shadow-lg shadow-accent/30 group"
                data-testid="success-cta-browse"
              >
                <Link to="/mock-exams">
                  {g("ss_cta_primary", "Browse Mock Exams")}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="btn-pill border-2 border-white/50 text-white hover:bg-white/10 hover:border-white text-base h-14"
              >
                <Link to="/school-services">
                  {g("ss_cta_secondary", "Partner With Us")}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
